import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  listOrdersByMerchant,
  markItemReady,
  markOrderAllItemsReady,
} from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import { formatMoney } from "../utils/money";
import { orderLineDisplayLabel, orderItemThumbnailSrc } from "../utils/orderLineMedia";
import {
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  StatusPill,
  formatWhen,
  matchesStatusFilter,
  orderMatchesSearch,
  statusFilterOptions,
  PhoneLink,
} from "../components/orderKit";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

export function Fulfill() {
  const { merchantId } = useMerchantStore();
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  /** Order that just became fully packed - offers "Plan route" as the next step. */
  const [readyOrderId, setReadyOrderId] = useState(null);
  const [searchParams] = useSearchParams();
  const focusOrderId = Number(searchParams.get("order")) || null;
  const focusedRef = useRef(null);

  async function refresh() {
    setError("");
    if (!merchantId) {
      setOrders([]);
      return;
    }
    try {
      const os = await listOrdersByMerchant(merchantId);
      const sorted = [...(os || [])].sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
      setOrders(sorted);
      return sorted;
    } catch (err) {
      setError(friendlyError(err, t("orders.load_failed")));
    }
  }

  useEffect(() => {
    refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  // Opened from Orders → "Fulfil": bring that order into view once it has loaded.
  useEffect(() => {
    if (focusOrderId && focusedRef.current) {
      focusedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusOrderId, orders]);

  const filterOptions = useMemo(() => statusFilterOptions(orders), [orders]);
  const visible = useMemo(
    () => orders.filter((o) => matchesStatusFilter(o, filter) && orderMatchesSearch(o, search, orderLineDisplayLabel)),
    [orders, filter, search]
  );
  const readyCount = useMemo(
    () => orders.filter((o) => String(o.status || "").toUpperCase() === "READY").length,
    [orders]
  );

  async function handleLineReady(orderId, itemId, ready) {
    const key = `item-${itemId}`;
    setUpdating(key);
    try {
      await markItemReady(orderId, itemId, ready);
      const fresh = await refresh();
      const updated = (fresh || []).find((o) => o.id === orderId);
      if (ready && String(updated?.status || "").toUpperCase() === "READY") setReadyOrderId(orderId);
    } catch (err) {
      setError(friendlyError(err, t("fulfill.update_failed")));
    } finally {
      setUpdating(null);
    }
  }

  async function handleMarkAllReady(orderId, ready) {
    const key = `order-${orderId}`;
    setUpdating(key);
    try {
      await markOrderAllItemsReady(orderId, ready);
      const fresh = await refresh();
      const updated = (fresh || []).find((o) => o.id === orderId);
      if (ready && String(updated?.status || "").toUpperCase() === "READY") setReadyOrderId(orderId);
    } catch (err) {
      setError(friendlyError(err, t("fulfill.update_failed")));
    } finally {
      setUpdating(null);
    }
  }

  const allItemsReady = (order) =>
    order.items && order.items.length > 0 && order.items.every((i) => i.ready);
  /** Once an order is on a route its packing is final: the server refuses to unmark it. */
  const isRouteLocked = (order) =>
    ["PLANNED", "CONFIRMED", "BROADCASTED", "PICKED_UP", "DELIVERED"].includes(
      String(order?.status || "").toUpperCase()
    );

  if (!merchantId) {
    return (
      <div className="page-container">
        <div className="card">
          <p className="muted">{t("ready.sign_in")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={t("nav.orders")}
        title={t("nav.fulfill_orders")}
        lead={t("fulfill.lead")}
        actions={
          <button type="button" onClick={() => refresh()}>
            <i className="fa-solid fa-rotate-right" aria-hidden="true" /> {t("common.refresh")}
          </button>
        }
      />
      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}

      {readyOrderId ? (
        <div className="alert alert--success" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          <span>
            <strong>{t("fulfill.packed", { id: readyOrderId })}</strong>{" "}
            {readyCount > 1 ? t("fulfill.waiting_many", { n: readyCount }) : t("fulfill.plan_next")}
          </span>
          <Link className="button-link button-link--primary" to={`/ready-orders?select=${readyOrderId}`}>
            <i className="fa-solid fa-route" aria-hidden="true" /> {t("order.plan_route")}
          </Link>
          <button type="button" className="alert__close" onClick={() => setReadyOrderId(null)} aria-label={t("common.dismiss")}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <div className="list-toolbar">
        <FilterBar options={filterOptions} value={filter} onChange={setFilter} ariaLabel={t("orders.status_aria")} />
        <div className="list-toolbar__right">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t("orders.search_placeholder")}
            ariaLabel={t("orders.search_aria")}
          />
        </div>
        {readyCount > 0 ? (
          <Link className="button-link button-link--primary" to="/ready-orders">
            <i className="fa-solid fa-route" aria-hidden="true" />{" "}
            {readyCount === 1 ? t("fulfill.plan_for_one") : t("fulfill.plan_for_n", { n: readyCount })}
          </Link>
        ) : null}
      </div>

      <div>
        {visible.length === 0 ? (
          <EmptyState
            icon="fa-clipboard-check"
            title={orders.length === 0 ? t("orders.empty_title") : t("common.nothing_here")}
            message={
              orders.length === 0
                ? t("fulfill.empty_message")
                : search.trim()
                  ? t("fulfill.no_search_match", { q: search.trim() })
                  : t("fulfill.no_status_match")
            }
            action={
              orders.length === 0 ? null : (
                <button
                  type="button"
                  onClick={() => {
                    setFilter("all");
                    setSearch("");
                  }}
                >
                  {t("orders.show_all")}
                </button>
              )
            }
          />
        ) : (
          <div className="fulfill-orders">
            {visible.map((order) => (
              <div
                key={order.id}
                ref={order.id === focusOrderId ? focusedRef : undefined}
                className={`card fulfill-order-card${order.directDeliveryOnly ? " direct-delivery-order-card" : ""}${
                  order.id === focusOrderId ? " fulfill-order-card--focus" : ""
                }`}
              >
                <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div className="row wrap" style={{ gap: 10 }}>
                    <strong style={{ fontSize: 15 }}>{t("order.title", { id: order.id })}</strong>
                    <StatusPill status={order.status} />
                    <span className="muted" style={{ fontSize: 13 }}>
                      {formatWhen(order.createdAt)} · {t("common.amount_etb", { amount: formatMoney(order.total) })}
                    </span>
                    <PhoneLink phone={order.clientPhone} style={{ fontSize: 13 }} />
                    {order.directDeliveryOnly ? (
                      <span className="rom-direct-chip" style={{ marginBottom: 0 }}>
                        {t("order.direct_delivery")}
                      </span>
                    ) : null}
                    {isRouteLocked(order) ? (
                      <span
                        className="tag tag-success"
                        title={t("fulfill.locked_order")}
                      >
                        {t("fulfill.route_planned")}
                      </span>
                    ) : null}
                  </div>
                  <div className="row">
                    {String(order.status || "").toUpperCase() === "READY" ? (
                      <Link className="button-link button-link--primary" to={`/ready-orders?select=${order.id}`}>
                        <i className="fa-solid fa-route" aria-hidden="true" /> {t("order.plan_route")}
                      </Link>
                    ) : isRouteLocked(order) ? null : (
                      <button
                        type="button"
                        className="primary"
                        disabled={updating === `order-${order.id}` || allItemsReady(order)}
                        onClick={() => handleMarkAllReady(order.id, true)}
                      >
                        {updating === `order-${order.id}` ? t("forgot.updating") : t("fulfill.mark_all_ready")}
                      </button>
                    )}
                    {isRouteLocked(order) ? null : (
                      <button
                        type="button"
                        disabled={updating === `order-${order.id}`}
                        onClick={() => handleMarkAllReady(order.id, false)}
                      >
                        {t("fulfill.unmark_all")}
                      </button>
                    )}
                  </div>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>{t("status.ready")}</th>
                      <th>{t("fulfill.col_item")}</th>
                      <th>{t("fulfill.col_qty")}</th>
                      <th>{t("fulfill.col_unit_price")}</th>
                      <th>{t("fulfill.col_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item) => {
                      const locked = isRouteLocked(order);
                      const label = orderLineDisplayLabel(item);
                      return (
                        <tr key={item.id} className={item.ready ? "line-ready" : "line-pending"}>
                          <td>
                            {locked ? (
                              <i
                                className="fa-solid fa-lock muted"
                                title={t("fulfill.locked_line")}
                                aria-label={t("fulfill.locked_line")}
                              />
                            ) : (
                              <input
                                type="checkbox"
                                className="fulfill-checkbox"
                                checked={item.ready}
                                disabled={updating === `item-${item.id}`}
                                onChange={(e) => handleLineReady(order.id, item.id, e.target.checked)}
                              />
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <img
                                src={orderItemThumbnailSrc(item.imageUrl)}
                                alt=""
                                loading="lazy"
                                width={36}
                                height={36}
                                style={{ borderRadius: 10, objectFit: "cover", border: "1px solid var(--border-color)" }}
                              />
                              <div>
                                <div style={{ fontWeight: 600 }}>{label}</div>
                                {item.sku && label !== String(item.sku) ? (
                                  <div className="muted" style={{ fontSize: 12 }}>
                                    {item.sku}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td>{t("common.amount_etb", { amount: formatMoney(item.unitPrice) })}</td>
                          <td>
                            {item.ready ? (
                              <span className="success">{t("status.ready")}</span>
                            ) : (
                              <span className="muted">{t("fulfill.pending")}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
